import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Eye, Users, UserCheck, UserX, UserPlus, Repeat, Globe } from 'lucide-react';

const PERIODS = [
  { key: 'day', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'all', label: 'All Time' },
];

const fmt = (n) => Number(n || 0).toLocaleString();
const flag = (code) => {
  if (!code || code.length !== 2) return '🌐';
  try {
    return String.fromCodePoint(...[...code.toUpperCase()].map(c => 127397 + c.charCodeAt(0)));
  } catch { return '🌐'; }
};

export default function VisitsPanel() {
  const [period, setPeriod] = useState('day');
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setData(null); setError(false);
    api.get('/admin/visit-stats', { params: { period } })
      .then(r => setData(r.data)).catch(() => setError(true));
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const tiles = data ? [
    { key: 'total_visits', label: 'Total visits', value: fmt(data.total_visits), Icon: Eye, color: '#2C5545' },
    { key: 'unique_visitors', label: 'Unique visitors', value: fmt(data.unique_visitors), Icon: Users, color: '#7A9A78' },
    { key: 'logged_in_visits', label: 'Logged-in visits', value: fmt(data.logged_in_visits), Icon: UserCheck, color: '#D99E4A' },
    { key: 'anon_visits', label: 'Non-logged-in visits', value: fmt(data.anon_visits), Icon: UserX, color: '#8A8F87' },
    { key: 'new_visitors', label: 'New visitors', value: fmt(data.new_visitors), Icon: UserPlus, color: '#7A6FC8' },
    { key: 'returning_visitors', label: 'Returning visitors', value: fmt(data.returning_visitors), Icon: Repeat, color: '#C86F53' },
  ] : [];

  return (
    <div data-testid="visits-panel">
      <h2 className="font-display font-bold text-2xl text-brand-ink mb-1">Site Visits</h2>
      <p className="text-brand-slate text-sm mb-5">Traffic overview — logged-in vs guests, new vs returning, and visitor countries.</p>

      <div className="inline-flex rounded-2xl bg-black/5 p-1 mb-6" data-testid="visits-period-filter">
        {PERIODS.map(p => (
          <button
            key={p.key}
            data-testid={`visits-period-${p.key}`}
            onClick={() => setPeriod(p.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition ${period === p.key ? 'bg-brand text-white shadow' : 'text-brand-ink hover:bg-black/5'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {error && <div className="glass-panel rounded-3xl p-6 text-brand-slate" data-testid="visits-error">Could not load visit stats.</div>}

      {!error && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {tiles.map(({ key, label, value, Icon, color }) => (
              <div key={key} data-testid={`visit-stat-${key}`} className="glass-panel rounded-3xl p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: color + '22' }}>
                  <Icon className="w-6 h-6" style={{ color }} />
                </div>
                <div>
                  <div className="font-display font-black text-3xl text-brand-ink leading-none">{data ? value : '—'}</div>
                  <div className="text-xs text-brand-slate mt-1">{label}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 glass-panel rounded-3xl p-5" data-testid="visits-countries">
            <h3 className="font-display font-bold text-brand-ink mb-3 flex items-center gap-2"><Globe className="w-4 h-4 text-brand" /> Visits by country</h3>
            {data && data.countries.length === 0 && <p className="text-sm text-brand-slate">No visits recorded for this period yet.</p>}
            <div className="flex flex-wrap gap-2">
              {data?.countries.map((c) => (
                <span key={c.code} data-testid={`visit-country-${c.code}`} className="inline-flex items-center gap-2 rounded-full bg-black/5 px-3 py-1.5 text-sm">
                  <span>{flag(c.code)}</span>
                  <span className="font-medium text-brand-ink">{c.code}</span>
                  <span className="text-brand-dim">{c.name}</span>
                  <span className="font-bold text-brand-ink">{fmt(c.count)}</span>
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
